import { useCallback, useState } from "react";

const STORAGE_KEY = "hdv.sidebar.collapsed";

function readStored(): boolean | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === null ? null : value === "1";
  } catch {
    // Private browsing or blocked storage: fall back to the default.
    return null;
  }
}

/**
 * The user's choice about the sidebar — and nothing else.
 *
 * This hook used to decide the layout: it read `window.innerWidth`, compared it
 * to a threshold of its own, and told the sidebar what to be. That number and
 * the CSS breakpoints drifted apart the moment anyone zoomed the browser, and
 * then the sidebar and the tables disagreed about which mode they were in.
 *
 * Structure is now decided entirely in CSS (see `--breakpoint-desk`). The
 * sidebar is a 56 px rail at every width; `collapsed === false` only adds a
 * `desk:` class that widens it where there is actually room. So this preference
 * is inert below 1200 px — it cannot force a 240 px panel into a window that
 * cannot afford one, which is a state the user could not get out of, because
 * the button that would fix it lives inside the panel doing the damage.
 *
 * The default is expanded: the spec and the mockups show the sidebar with its
 * labels, and that is what a desktop user gets.
 */
export function useSidebar(): {
  collapsed: boolean;
  toggle: () => void;
} {
  const [collapsed, setCollapsed] = useState<boolean>(() => readStored() ?? false);

  const toggle = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* see readStored */
      }
      return next;
    });
  }, []);

  return { collapsed, toggle };
}
